import { defineComponent as _defineComponent } from 'vue'
import mapboxgl from 'mapbox-gl'
import { computed, watch } from 'vue'
import GeoJsonSource from '../mapbox/GeoJsonSource.vue'
import LayerGroup from '../mapbox/LayerGroup.vue'
import { useJobsGeoJSON } from '../composables/useJobsGeoJSON'
import { useJobFilterStore } from '../JobsFilterStore'
import { useFilteredJobStoreLegacyAdapter } from '@/components/jobs/job-search-page/filteredJobStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'JobsLayer',
  props: {
    layerId: null,
    sourceId: null,
    before: null,
    hideLayer: { type: Boolean },
    hiddenLocationIds: null
  },
  emits: ["click"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    event: 'click',
    data: mapboxgl.MapboxGeoJSONFeature[],
    eventData: mapboxgl.MapMouseEvent & mapboxgl.EventData
  )
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  layerId: string
  sourceId: string
  before?: string
  hideLayer?: boolean
  hiddenLocationIds?: (string | number)[]
}





const jobsFilterStore = useJobFilterStore()
const legacyJobStore = useFilteredJobStoreLegacyAdapter()

// Use the extracted composable
const { geoJsonData, resetAndRefetch, isComponentMounted, error } =
  useJobsGeoJSON()

// Only show layers when we have data, component is mounted, and not in error state
const showLayers = computed(() => {
  return (
    isComponentMounted.value &&
    !error.value &&
    geoJsonData.value &&
    geoJsonData.value.features &&
    geoJsonData.value.features.length > 0
  )
})

// Reset last modified when filters change (force full reload)
watch(
  [
    legacyJobStore.selectedStart,
    legacyJobStore.selectedEnd,
    legacyJobStore.selectedDivisions,
    legacyJobStore.selectedServiceProviders,
    legacyJobStore.selectedJobStatuses,
    legacyJobStore.selectedApprovalStatuses,
    legacyJobStore.selectedResourceTypes,
    legacyJobStore.selectedActivities,
    legacyJobStore.selectedPriorities,
    legacyJobStore.selectedLocations,
    jobsFilterStore.selectedBatchStatuses,
  ],
  () => {
    resetAndRefetch()
  }
)

const handleClick = (
  data: mapboxgl.MapboxGeoJSONFeature[],
  eventData: mapboxgl.MapMouseEvent & mapboxgl.EventData
) => {
  emits('click', data, eventData)
}

const clusterProperties = {
  // Sum all TotalCount values in the cluster
  TotalCount: ['+', ['get', 'TotalCount']],
  // Sum pickup totals
  PickupTotal: ['+', ['get', ['get', 'Total', ['get', 'Pickup']]]],
  // Sum dropoff totals
  DropoffTotal: ['+', ['get', ['get', 'Total', ['get', 'Dropoff']]]],
}

const layers = computed<mapboxgl.SymbolLayer[]>(() => {
  return [
    {
      id: props.layerId,
      type: 'symbol',
      source: props.sourceId,
      filter: ['!', ['has', 'point_count']], // Only show individual points
      layout: {
        'icon-allow-overlap': true,
        'icon-image': [
          'concat',
          'job-cluster',
          '__',
          '#eeeeee',
          '__',
          ['get', 'TotalCount'],
        ],
        'text-field': ['get', 'LocationName'],
        'text-font': ['Montserrat Medium', 'Arial Unicode MS Regular'],
        'text-radial-offset': 2,
        'text-line-height': 1.1,
        'text-allow-overlap': false,
        'text-optional': true,
        'text-anchor': 'top',
        'text-letter-spacing': 0.1,
        'text-size': {
          stops: [
            [14, 0],
            [15, 11],
            [15.5, 12],
            [17, 14],
          ],
        },
      },
      paint: {
        'text-halo-color': 'hsl(0, 5%, 0%)',
        'text-color': 'hsl(0, 0%, 100%)',
        'text-halo-width': 2,
        'text-halo-blur': 0.5,
        'text-opacity': {
          stops: [
            [15.5, 0],
            [16, 1],
          ],
        },
      },
    },
    {
      id: 'cluster-count',
      type: 'symbol',
      source: props.sourceId,
      filter: ['has', 'point_count'], // Only show clusters
      layout: {
        'text-field': ['get', 'TotalCount'],
        'text-optional': false,
        'text-allow-overlap': true,
        'text-font': ['Montserrat Bold', 'Arial Unicode MS Regular'],
        'text-size': 12,
      },
    },
  ] as mapboxgl.SymbolLayer[]
})

const filters = computed(() => {
  const filter: any[] = ['all']

  const ids = props.hiddenLocationIds || []
  if (ids.length > 0) {
    filter.push(['!', ['in', ['get', 'LocationId'], ['literal', ids]]])
  }

  return filter
})

return { __sfc: true,props, emits, jobsFilterStore, legacyJobStore, geoJsonData, resetAndRefetch, isComponentMounted, error, showLayers, handleClick, clusterProperties, layers, filters, GeoJsonSource, LayerGroup }
}

})